import type { SpinningWheelUIReward } from "@cede/types";
import { GRAND_PRIZE_REWARDS_THRESHOLDS, REWARD_SYMBOL, type RewardSymbol } from "@/constants/spinningWheel";
import Decimal from "decimal.js";

/**
 * Utility functions for the spinning wheel feature
 */

/**
 * Checks if a reward is a "big prize" (jackpot reward)
 */
export function isBigPrize(rewardAmount: Decimal, rewardSymbol: string): boolean {
  const symbol = rewardSymbol as RewardSymbol;
  if (!GRAND_PRIZE_REWARDS_THRESHOLDS[symbol]) {
    return false;
  }
  return new Decimal(GRAND_PRIZE_REWARDS_THRESHOLDS[symbol]).lessThanOrEqualTo(rewardAmount);
}

/**
 * Generates tweet text for sharing a reward
 */
export function generateTweetText(reward: SpinningWheelUIReward): string {
  const isBig = isBigPrize(reward.rewardAmount, reward.rewardSymbol);

  if (isBig) {
    return `Just hit the jackpot on The Wheel at @CedeHub! Scored ${reward.rewardAmount.toNumber()} ${
      reward.rewardSymbol
    }\n\nTry your luck and spin now #CedeHub #SpinTheWheel`;
  }

  return `I just spun The Wheel on @CedeHub and won ${reward.rewardAmount.toNumber()} ${
    reward.rewardSymbol
  }\n\nCome try your luck and spin now! #CedeHub #SpinTheWheel`;
}

/**
 * Formats a date as DD/MM/YYYY HH:MM UTC
 */
export function formatDateUTC(date: Date): string {
  const day = date.getUTCDate().toString().padStart(2, "0");
  const month = (date.getUTCMonth() + 1).toString().padStart(2, "0");
  const year = date.getUTCFullYear();
  const hours = date.getUTCHours().toString().padStart(2, "0");
  const minutes = date.getUTCMinutes().toString().padStart(2, "0");
  return `${day}/${month}/${year} ${hours}:${minutes} UTC`;
}

/**
 * Finds the biggest win from an array of rewards
 */
export function getBiggestWin(results: SpinningWheelUIReward[]): SpinningWheelUIReward {
  return results.reduce((biggest, current) => {
    if (current.rewardSymbol === REWARD_SYMBOL.WETH) {
      return current;
    }
    return current.rewardAmount.greaterThan(biggest.rewardAmount) ? current : biggest;
  });
}

/**
 * Sorts rewards from smallest to largest
 */
export function sortRewardsBySize(rewards: SpinningWheelUIReward[]): SpinningWheelUIReward[] {
  return [...rewards].sort((a, b) => {
    if (a.rewardSymbol === REWARD_SYMBOL.WETH) {
      return 1;
    }
    return a.rewardAmount.greaterThan(b.rewardAmount) ? 1 : -1;
  });
}

/**
 * Calculates the total amount of rewards by symbol
 */
export function calculateRewardTotals(results: Array<{ rewardSymbol: string; rewardAmount: Decimal }>): {
  usdt: Decimal;
  eth: Decimal;
  usdc: Decimal;
} {
  const usdt = results.reduce((acc, result) => {
    if (result.rewardSymbol === REWARD_SYMBOL.USDT) {
      return acc.add(result.rewardAmount);
    }
    return acc;
  }, new Decimal(0));
  const eth = results.reduce((acc, result) => {
    if (result.rewardSymbol === REWARD_SYMBOL.WETH) {
      return acc.add(result.rewardAmount);
    }
    return acc;
  }, new Decimal(0));
  const usdc = results.reduce((acc, result) => {
    if (result.rewardSymbol === REWARD_SYMBOL.USDC) {
      return acc.add(result.rewardAmount);
    }
    return acc;
  }, new Decimal(0));
  return { usdt, eth, usdc };
}

/**
 * Separates and calculates reward totals by currency type for analytics
 * Returns detailed breakdown including counts and amounts for each currency
 */
export function calculateRewardAnalytics(rewards: SpinningWheelUIReward[]): {
  totalUSDT: Decimal;
  totalETH: Decimal; // represents WETH totals for backward naming compatibility
  totalUSDC: Decimal;
  usdtWinCount: number;
  ethWinCount: number; // represents WETH count
  usdcWinCount: number;
  usdtRewards: SpinningWheelUIReward[];
  ethRewards: SpinningWheelUIReward[]; // WETH rewards
  usdcRewards: SpinningWheelUIReward[];
} {
  const usdtRewards = rewards.filter((result) => result.rewardSymbol === REWARD_SYMBOL.USDT);
  const ethRewards = rewards.filter((result) => result.rewardSymbol === REWARD_SYMBOL.WETH);
  const usdcRewards = rewards.filter((result) => result.rewardSymbol === REWARD_SYMBOL.USDC);

  return {
    totalUSDT: usdtRewards.reduce((acc, result) => acc.add(result.rewardAmount), new Decimal(0)),
    totalETH: ethRewards.reduce((acc, result) => acc.add(result.rewardAmount), new Decimal(0)),
    totalUSDC: usdcRewards.reduce((acc, result) => acc.add(result.rewardAmount), new Decimal(0)),
    usdtWinCount: usdtRewards.length,
    ethWinCount: ethRewards.length,
    usdcWinCount: usdcRewards.length,
    usdtRewards,
    ethRewards,
    usdcRewards,
  };
}

/**
 * Determines how many icons to render for a given reward amount.
 * Bins are:
 *  - 0 < x < 2   -> 1
 *  - 2 <= x < 5  -> 2
 *  - 5 <= x < 20 -> 3
 *  - 20 <= x < 100 -> 5
 *  - x >= 100    -> 8
 */
export const getIconCountForAmount = (amount: Decimal): number => {
  if (amount.lessThan(1)) return 0;
  if (amount.lessThan(2)) return 1;
  if (amount.lessThan(5)) return 2;
  if (amount.lessThan(20)) return 3;
  if (amount.lessThan(100)) return 5;
  return 8;
};
